<template>
    <Modal @cancel="cancel">
        <div>
            <div class="header-container">
                <h3 class="header-title">
                    {{ $t('download.selection.modal.header') }}
                    <img :src="closeIcon" alt="close" class="close-icon" @click="cancel" />
                </h3>
            </div>

            <div class="card-content-container">
                <div v-if="nbDocumentsModal > 100" class="error">
                    {{ $t('download.selection.modal.nombre.important') }}
                </div>

                <div class="date-container">
                    <div v-if="filter.documentDateFrom !== undefined" class="dateInputWrap">
                        <DatePicker
                            :dateMax="filter.documentDateTo"
                            :dateValue="filter.documentDateFrom"
                            :label="$t('label.documentDateFrom')"
                            @setDate="setDateFrom"
                        />
                    </div>
                    <div v-if="filter.documentDateTo !== undefined" class="dateInputWrap">
                        <DatePicker
                            :dateMin="filter.documentDateFrom"
                            :dateValue="filter.documentDateTo"
                            :label="$t('label.documentDateTo')"
                            @setDate="setDateTo"
                        />
                    </div>
                </div>
                <div v-if="documentTypeList !== undefined" class="date-container">
                    <DropDownMultiple
                        :checkboxDisplay="this.documentTypeCheckboxDisplay"
                        :inputDisplay="this.filterDocumentTypeDisplay"
                        :isChecked="this.isCheckedDocumentType"
                        :label="$t('label.byDocumentType')"
                        :list="documentTypeList"
                        :listTotal="documentTypeList"
                        :selectedItems="filter.documentTypeList"
                        @add="addSelectDocumentType"
                        @remove="removeSelectDocumentType"
                        @toggleSelectAll="toggleSelectAll"
                    />
                </div>

                <div class="button-container">
                    <ButtonBlue
                        :disabled="isMissingData"
                        :onClick="valider"
                        :text="$t('download.selection.modal.button.valider')"
                        class="button-confirm-download"
                        name="confirm"
                    />
                </div>
            </div>
        </div>
    </Modal>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import DatePicker from '@/components/ui-components/DatePicker.vue';
import DropDownMultiple from '@/components/ui-components/DropDownMultiple.vue';
import Modal from '@/components/ui-components/Modal.vue';
import ButtonBlue from '@/components/ui-components/ButtonBlue.vue';
import { TypeDocumentReglementaire } from '@/models/TypeDocumentReglementaire';
import dayjs from 'dayjs';

interface Data {
    filter: {
        documentDateFrom: string;
        documentDateTo: string;
        documentTypeList: TypeDocumentReglementaire[];
    };
}

export default defineComponent({
    name: 'ModalDownloadDocuments',
    data(): Data {
        return {
            filter: {
                documentDateFrom: dayjs().subtract(3, 'month').format('YYYY-MM-DD'),
                documentDateTo: dayjs().format('YYYY-MM-DD'),
                documentTypeList: [],
            },
        };
    },
    components: {
        DatePicker,
        DropDownMultiple,
        Modal,
        ButtonBlue,
    },
    props: {
        documentTypeList: { type: Array as PropType<TypeDocumentReglementaire[]>, required: false },
        nbDocumentsModal: { type: Number, required: true },
    },
    methods: {
        cancel() {
            this.$emit('cancel');
        },
        filterDocumentTypeDisplay(items: TypeDocumentReglementaire[], list: string[]): string[] {
            if (items.length === list.length) return [this.$t('all')];
            return items.length > 2
                ? [...items.map(x => this.$t(`document.type.${x.id}`)).slice(0, 2)]
                : items.map(x => {
                      return this.$t(`document.type.${x.id}`);
                  });
        },
        isCheckedDocumentType(status: TypeDocumentReglementaire): boolean {
            return !!this.filter.documentTypeList.find(
                (item: TypeDocumentReglementaire) => item.code === status.code
            );
        },
        documentTypeCheckboxDisplay(item: TypeDocumentReglementaire): string {
            if (item.id !== null && item.id < 10) {
                return this.$t(`document.type.${item.id}`);
            }
            return item.libelle;
        },
        setDateFrom(value: string): void {
            this.filter.documentDateFrom = value;
        },
        addSelectDocumentType(documentType: TypeDocumentReglementaire): void {
            this.filter.documentTypeList.push(documentType);
        },
        removeSelectDocumentType(documentType: TypeDocumentReglementaire): void {
            this.filter.documentTypeList.splice(this.filter.documentTypeList.indexOf(documentType), 1);
        },
        toggleSelectAll(): void {
            if (this.filter.documentTypeList.length === 0) {
                this.filter.documentTypeList = this.documentTypeList == undefined ? [] : this.documentTypeList;
            } else {
                this.filter.documentTypeList = [];
            }
        },
        setDateTo(value: string): void {
            this.filter.documentDateTo = value;
        },
        valider(): void {
            this.$emit('downloadDocuments', this.filter);
        },
    },
    computed: {
        closeIcon() {
            return require('@/assets/img/close-white.svg');
        },
        isMissingData(): boolean {
            return (
                this.filter.documentDateTo === '' ||
                this.filter.documentDateFrom === '' ||
                (this.documentTypeList === undefined ? false : this.filter.documentTypeList.length === 0)
            );
        },
    },
});
</script>

<style scoped>
.header-container {
    position: relative;
    padding: 2px 24px;
    background-color: var(--bleu-prinicipal);
    color: white;
}
.card-content-container {
    padding: 16px;
    background-color: var(--white);
    overflow: hidden;
    max-height: 60vh;
}
.close-icon {
    cursor: pointer;
    float: right;
}
.button-container {
    float: right;
    margin: 15px;
}
.button-confirm-download {
    cursor: pointer;
    height: 38px;
    width: 95px;
    border-radius: 6px;
}
.dateInputWrap {
    width: 250px;
}
.date-container {
    display: flex;
    justify-content: space-around;
    width: 550px;
    margin-top: 20px;
}
:deep(.drop-container) {
    position: unset;
}
.error {
    font-size: 12px;
    color: var(--red);
}
</style>
